import { Carousel, Icon, ICONS, useIsWindowBiggerThan } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CopyToClipboard } from '@websrc/App/Components/CopyToClipboard'
import { CTA } from '@websrc/App/Components/CTA'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapLessonSelector } from '@websrc/DataMappers/mapLessonSelector'
import clsx from 'clsx'
import { box, breakpoints, styles, typography } from 'leds'
import React, { FC, useLayoutEffect, useState } from 'react'

export type LessonSelectorProps = {
  selector: ReturnType<typeof mapLessonSelector>
  className?: string
  useBoxTypeText?: boolean
}

export const LessonSelector: FC<LessonSelectorProps> = ({
  selector,
  className,
  useBoxTypeText,
}) => {
  const t = useStaticText()
  const [itemsPerPage, setItemsPerPage] = useState(2)
  const mediumWindow = useIsWindowBiggerThan(breakpoints.m)

  useLayoutEffect(() => {
    if (!mediumWindow) {
      setItemsPerPage(1)
    }
  }, [setItemsPerPage, mediumWindow])

  return (
    <div
      className={clsx(
        box({ type: useBoxTypeText ? 'text' : undefined, align: 'center' }),
        className
      )}
    >
      {selector.showHeadline && (
        <h2 className={typography({ responsiveSize: 'xl', weight: 'bold' })} id={selector.headline}>
          {selector.headline} <CopyToClipboard id={selector.headline} />
        </h2>
      )}
      {selector.description && (
        <Markdown
          className={styles({ marginTop: 25, marginBottom: 0 })}
          markdown={selector.description}
        />
      )}
      <Carousel
        className={styles({ marginTop: 48, marginBottom: 0 })}
        displayBlockOnPrint={true}
        itemsPerPage={itemsPerPage}
      >
        {selector.items.map((item, key) => (
          <span
            className={styles({
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
            data-testid={'lesson-selector-item'}
            key={key}
          >
            <div className={styles({ background: 'grey2' })}>
              <div
                className={clsx(
                  typography({ size: 'sm', color: 'grey' }),
                  styles({ paddingX: 10, paddingY: 5 })
                )}
              >
                {item.unitTitle}
              </div>
              <Asset
                sizes={`(max-width: ${breakpoints.s}px) 90vw, 550px`}
                srcSetWidths={[400, 600, 800]}
                thumbnail={item.image}
              />
            </div>
            <h6 className={typography({ size: 'xl2', weight: 'bold' })}>{item.title}</h6>
            <div className={styles({ marginY: 10 })}>
              {item.subjects.map((subject, subjectKey) => (
                <span
                  className={clsx(
                    styles({ marginRight: 5, marginTop: 5, padding: 5, display: 'inline-flex' }),
                    box({ border: 'full', borderColor: 'grey4', rounded: 'small' }),
                    typography({ size: 'sm', color: 'grey' })
                  )}
                  key={subjectKey}
                >
                  {subject}
                </span>
              ))}
            </div>
            <div className={typography({ size: 'sm', color: 'grey' })}>
              <span className={styles({ marginRight: 10 })}>
                <Icon icon={ICONS.CLOCK} size={12} />
                <span className={styles({ paddingLeft: 5 })}>{item.duration}</span>
              </span>
              <span>
                <Icon icon={ICONS.GRADUATE} size={12} />
                <span className={styles({ paddingLeft: 5 })}>{item.grades}</span>
              </span>
            </div>
            <div className={styles({ marginY: 10 })}>
              <CTA
                colorTheme={'blueInverse'}
                cta={{
                  __ctaType: 'internal',
                  url: item.url,
                  text: t.plans_lessons_worksheets.start_lesson.toUpperCase(),
                  newTab: false,
                }}
                fontWeight={'bold'}
                prefix={
                  <Icon className={styles({ marginRight: 5 })} icon={ICONS.ARROW_RIGHT} size={14} />
                }
              />
            </div>
          </span>
        ))}
      </Carousel>
    </div>
  )
}
